import { LocatorInput } from '../../models/object-mapping.model';

/**
 * Art eines Kandidaten, wie Testerinnen sie im Bundle sehen (`OB_ARTEN_TECH`, NEX-2448). Der Name
 * ist zugleich der Schluessel des Labels unter `objectMappings.state.kind.*`.
 */
export type LocatorKind =
  | 'TESTID'
  | 'ROLE'
  | 'ID'
  | 'CSS'
  | 'XPATH'
  | 'TEXT'
  | 'NAME'
  | 'AUTOMATIONID'
  | 'CLASSNAME'
  | 'SAPID';

/** Gelesen (`ObjectMappingLocator`) oder geschrieben (`LocatorInput`). */
export interface StoredLocator {
  readonly strategy?: string;
  readonly value?: string;
  readonly name?: string;
}

/** `label` ist ein i18n-Schluessel oder, bei einer unbekannten Strategie, ihr Rohname. */
export interface ReadLocator {
  kind: LocatorKind | null;
  label: string;
  value: string;
}

const WEB_KINDS: readonly LocatorKind[] = ['TESTID', 'ROLE', 'ID', 'CSS', 'XPATH', 'TEXT', 'NAME'];

/** Engines mit eigenen Strategien (`LocatorRules.strategiesFor` im BE); alle anderen nutzen die Web-Liste. */
const KINDS_BY_ENGINE: Readonly<Record<string, readonly LocatorKind[]>> = {
  windows: ['AUTOMATIONID', 'NAME', 'CLASSNAME', 'XPATH'],
  sap: ['SAPID', 'NAME', 'TEXT'],
};

const KIND_OF_STRATEGY: Readonly<Record<string, LocatorKind>> = {
  css: 'CSS',
  xpath: 'XPATH',
  role: 'ROLE',
  text: 'TEXT',
  name: 'NAME',
  automationid: 'AUTOMATIONID',
  classname: 'CLASSNAME',
  sapid: 'SAPID',
};

/** Bestand ohne Art im Dialog, aber mit eigenem Label. */
const LABELLED_STRATEGIES = new Set(['label']);

/** Rolle in einem Feld (A16); der Name reicht bis zum letzten `']`, so bleiben auch Anfuehrungszeichen erhalten. */
const ROLE_FORM = /^([A-Za-z]+)(?:\[name='(.*)'\])?$/s;
const TESTID_FORM = /^\[data-testid="((?:[^"\\]|\\.)*)"\]$/s;
const NAME_FORM = /^\[name="((?:[^"\\]|\\.)*)"\]$/s;

export function kindLabelKey(kind: string): string {
  return `objectMappings.state.kind.${kind}`;
}

/** `button` oder `button[name='Anmelden']`; Leerraum am Rand zaehlt nicht. */
export function isRoleValue(value: string): boolean {
  return ROLE_FORM.test(value.trim());
}

export function locatorKindsFor(engineId: string | null | undefined): readonly LocatorKind[] {
  return (engineId && KINDS_BY_ENGINE[engineId]) || WEB_KINDS;
}

/**
 * Web schreibt Test-ID, ID und Name als CSS in den Formen, die die Engine selbst erzeugt
 * (`engine-core/src/tcdl/fulltag.ts`) und als Trefferart erkennt; sonst ist die Art die Strategie.
 */
export function toLocator(kind: LocatorKind, value: string, engineId: string | null | undefined): LocatorInput {
  if (isWeb(engineId)) {
    if (kind === 'TESTID') return { strategy: 'css', value: `[data-testid="${escapeCssValue(value)}"]` };
    if (kind === 'ID') return { strategy: 'css', value: `#${CSS.escape(value)}` };
    if (kind === 'NAME') return { strategy: 'css', value: `[name="${escapeCssValue(value)}"]` };
  }
  if (kind === 'ROLE') return toRoleLocator(value);
  return { strategy: kind.toLowerCase(), value };
}

/**
 * Eine besondere Web-Art gibt es nur, wenn das Zurueckschreiben genau denselben Wert ergibt;
 * so bleibt jeder gespeicherte Wert verlustfrei, alles andere heisst „CSS“.
 */
export function readLocator(locator: StoredLocator, engineId: string | null | undefined): ReadLocator {
  const strategy = locator.strategy ?? '';
  const value = locator.value ?? '';
  if (strategy === 'css' && isWeb(engineId)) {
    const special = readWebCss(value, engineId);
    if (special) return special;
  }
  if (strategy === 'role') return read('ROLE', joinRole(value, locator.name));
  const kind = KIND_OF_STRATEGY[strategy];
  if (kind) return read(kind, value);
  return {
    kind: null,
    label: LABELLED_STRATEGIES.has(strategy) ? kindLabelKey(strategy.toUpperCase()) : strategy,
    value,
  };
}

function readWebCss(css: string, engineId: string | null | undefined): ReadLocator | null {
  const forms: [LocatorKind, string | null][] = [
    ['TESTID', TESTID_FORM.exec(css)?.[1] ?? null],
    ['NAME', NAME_FORM.exec(css)?.[1] ?? null],
    ['ID', css.startsWith('#') ? css.slice(1) : null],
  ];
  for (const [kind, raw] of forms) {
    if (raw === null) continue;
    const plain = kind === 'ID' ? unescapeCssIdentifier(raw) : unescapeCssValue(raw);
    if (toLocator(kind, plain, engineId).value === css) return read(kind, plain);
  }
  return null;
}

/** Das BE speichert Rolle und Name getrennt (`strategy: role`, `value`, `name`). */
function toRoleLocator(value: string): LocatorInput {
  const match = ROLE_FORM.exec(value.trim());
  if (!match) return { strategy: 'role', value };
  const [, role, name] = match;
  return name ? { strategy: 'role', value: role, name } : { strategy: 'role', value: role };
}

function joinRole(role: string, name: string | undefined): string {
  return name ? `${role}[name='${name}']` : role;
}

function read(kind: LocatorKind, value: string): ReadLocator {
  return { kind, label: kindLabelKey(kind), value };
}

function isWeb(engineId: string | null | undefined): boolean {
  return !engineId || !(engineId in KINDS_BY_ENGINE);
}

/** Wie `escapeCssValue` der Engine: Anfuehrungszeichen und Backslash im Attributwert. */
function escapeCssValue(value: string): string {
  return value.replace(/(["\\])/g, '\\$1');
}

function unescapeCssValue(value: string): string {
  return value.replace(/\\(.)/gs, '$1');
}

function unescapeCssIdentifier(value: string): string {
  return value.replace(/\\([0-9a-fA-F]{1,6}) ?|\\(.)/gs, (_, hex: string | undefined, char: string | undefined) =>
    hex ? String.fromCodePoint(parseInt(hex, 16)) : (char ?? ''),
  );
}
